import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box, Button, Card, IconButton, InputAdornment, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ClearIcon from '@mui/icons-material/Clear'
import SearchIcon from '@mui/icons-material/Search'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import EmptyState from '../merchants/shared/EmptyState'
import { ReadinessChip } from '../merchants/shared/StateChip'
import { C, SX, TYPE } from '../merchants/shared/tokens'
import { errorText, fetchMerchants } from './ptlSetupApi'
import { ROUTES, num } from './constants'
import { areaStatus, isFullySetUp, statusOf } from './components/MerchantStatus'
import { Banner, merchantLabel, changeText } from './components/SetupUi'
import PtlPage from './shell/PtlPage'

/** KPI cards double as the list filter (?view=). */
const VIEWS = [
  { key: 'all', label: 'Live PTL merchants', test: () => true },
  { key: 'attention', label: 'Needs set-up', test: (m) => !isFullySetUp(m) },
  { key: 'custom-tat', label: 'With custom TAT', test: (m) => statusOf(m).customTat },
  { key: 'own-map', label: 'With own zone map', test: (m) => statusOf(m).ownMap },
]

const PAGE_SIZES = [25, 50, 100]

/** Old prototype links (?merchantId=&tab=) open the new screens. */
const OLD_TAB_ROUTE = {
  pricing: (id) => ROUTES.updateStep(id, 'pricing'),
  tat: (id) => ROUTES.updateStep(id, 'tat'),
  allocation: (id) => ROUTES.updateStep(id, 'allocation'),
  'zone-map': (id) => `${ROUTES.zoneOverrides}/${id}`,
}

const lastChange = (m) => [m.zoneMapLatest, m.tatLatest].filter(Boolean).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]

/**
 * PTL FREIGHT → PTL Merchant Setup → All Merchants → Merchant Update (plan screen 11). Every live PTL merchant with the
 * state of its rate cards, TAT, allocation couriers and zone map; a row opens its hub (screen 12). ?view= and ?q= keep
 * the list in the link.
 */
export default function PtlMerchantUpdate() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [merchants, setMerchants] = useState(null)
  const [error, setError] = useState('')
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(PAGE_SIZES[0])

  const oldId = params.get('merchantId')
  const view = VIEWS.some((v) => v.key === params.get('view')) ? params.get('view') : 'all'
  const search = params.get('q') || ''
  const setParam = (k, v) => {
    const next = new URLSearchParams(params)
    if (v && !(k === 'view' && v === 'all')) next.set(k, v)
    else next.delete(k)
    setParams(next, { replace: true })
    setPage(0)
  }

  const load = useCallback(() => {
    setError('')
    fetchMerchants().then(setMerchants).catch((e) => setError(errorText(e, 'Could not load PTL merchants')))
  }, [])
  useEffect(() => { if (!oldId) load() }, [load, oldId])

  const all = useMemo(() => merchants || [], [merchants])
  const counts = useMemo(() => Object.fromEntries(VIEWS.map((v) => [v.key, all.filter(v.test).length])), [all])
  const rows = useMemo(() => {
    const v = VIEWS.find((x) => x.key === view)
    const q = search.trim().toLowerCase()
    return all.filter((m) => v.test(m) && (!q || merchantLabel(m).toLowerCase().includes(q) || m.id.toLowerCase().includes(q)))
  }, [all, view, search])

  if (oldId) {
    const to = (OLD_TAB_ROUTE[params.get('tab')] || ROUTES.updateHub)(oldId)
    return <Navigate replace to={to} />
  }

  const kpi = (key, extra) => ({
    key, label: VIEWS.find((v) => v.key === key).label, value: merchants ? num(counts[key]) : '', active: view === key,
    onClick: () => setParam('view', view === key ? 'all' : key), ...extra,
  })
  const kpis = [
    kpi('all', { sub: 'PTL merchant type on', onClick: () => setParam('view', 'all') }),
    kpi('attention', { sub: 'No rate card yet', tone: counts.attention ? 'warn' : 'ok' }),
    kpi('custom-tat', { sub: 'Agreement TAT on some lanes' }),
    kpi('own-map', { sub: 'Own zone for some pincodes' }),
  ]

  return (
    <PtlPage title='Merchant Update'
      description='Change a live PTL merchant after onboarding: its rate cards, TAT, allocation couriers and zone map. Open a merchant to see what is set and change one area at a time.'
      kpis={kpis} kpisLoading={!merchants && !error}>
      {error && <Banner tone='danger' action={<Button size='small' onClick={load} sx={{ textTransform: 'none' }}>Retry</Button>}>{error}</Banner>}

      {merchants && all.length === 0 ? (
        <EmptyState title='No PTL merchants yet' body='A merchant appears here once it is onboarded from Onboarding → Onboard a merchant.' />
      ) : (
        <Card elevation={0} sx={{ ...SX.card, overflow: 'hidden' }}>
          <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${C.line}`, bgcolor: C.surfaceSunken }}>
            <TextField size='small' placeholder='Search merchant or id' value={search} onChange={(e) => setParam('q', e.target.value)}
              sx={{ width: { xs: '100%', sm: 320 }, bgcolor: C.surface }} inputProps={{ 'aria-label': 'Search merchant' }}
              InputProps={{
                startAdornment: <InputAdornment position='start'><SearchIcon sx={{ fontSize: 18, color: C.inkGhost }} /></InputAdornment>,
                endAdornment: search ? (
                  <InputAdornment position='end'>
                    <IconButton size='small' aria-label='Clear search' edge='end' onClick={() => setParam('q', '')}><ClearIcon fontSize='small' /></IconButton>
                  </InputAdornment>
                ) : null,
              }} />
            <Typography role='status' sx={{ ...TYPE.rowHelp, color: C.inkMuted }}>
              {merchants ? `${num(rows.length)} of ${num(all.length)} merchants${view !== 'all' ? ` · ${VIEWS.find((v) => v.key === view).label}` : ''}` : 'Loading merchants…'}
            </Typography>
          </Box>

          {merchants && rows.length === 0 ? (
            <Box sx={{ p: 2 }}><EmptyState dense title='No merchants match' body={search ? 'Try another name or id, or clear the search.' : 'No merchant is in this view.'} /></Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size='small' sx={{ minWidth: 880 }} aria-label='Live PTL merchants and their set-up'>
                <TableHead>
                  <TableRow>
                    {['Merchant', 'Rate cards', 'TAT', 'Allocation', 'Zone map', 'Last TAT / zone change', ''].map((h, i) => (
                      <TableCell key={i} sx={{ ...TYPE.eyebrow, fontSize: 10, color: C.inkFaint, bgcolor: C.surfaceAlt, borderColor: C.line, py: 1.25, whiteSpace: 'nowrap' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.slice(page * size, page * size + size).map((m) => {
                    const s = areaStatus(m)
                    return (
                      <TableRow key={m.id} hover onClick={() => navigate(ROUTES.updateHub(m.id))} sx={{ cursor: 'pointer', '& td': { borderColor: C.lineSoft, py: 1.25 } }}>
                        <TableCell sx={{ minWidth: 200 }}>
                          <Typography sx={TYPE.rowLabel}>{m.name}</Typography>
                          {m.companyName && m.companyName !== m.name && <Typography sx={{ ...TYPE.meta, color: C.inkFaint }}>{m.companyName}</Typography>}
                        </TableCell>
                        {['pricing', 'tat', 'allocation', 'zone'].map((k) => <TableCell key={k}><ReadinessChip tone={s[k].tone} label={s[k].label} /></TableCell>)}
                        <TableCell sx={{ fontSize: 12, color: C.inkMuted, whiteSpace: 'nowrap' }}>{changeText(lastChange(m))}</TableCell>
                        <TableCell align='right'>
                          <Button size='small' variant='outlined' endIcon={<ChevronRightIcon />} onClick={(e) => { e.stopPropagation(); navigate(ROUTES.updateHub(m.id)) }}
                            sx={{ ...SX.ctaGhost, fontSize: 12.5, minHeight: 32 }}>Open</Button>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
          {rows.length > 0 && (
            <TablePagination component='div' count={rows.length} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={size}
              onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0) }} rowsPerPageOptions={PAGE_SIZES} sx={{ borderTop: `1px solid ${C.line}` }} />
          )}
        </Card>
      )}
    </PtlPage>
  )
}
